import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { ActivatedRoute, Router } from '@angular/router';

import { HttpService } from '../shared/services/http.service';

import { Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TreeDataService } from '../tree-data.service';
import { DragDropService } from '../drag-drop.service';
import { TreeItem, TreeLevel, TreeType, HContent } from '../tree.model';
import { TreeComponent } from '../tree/tree.component';
import { BuildVersionsComponent } from './build-versions/build-versions.component';
import { PropertiesPanelComponent } from './properties-panel/properties-panel.component';
import { EditorToolbarComponent } from './editor-toolbar/editor-toolbar.component';
import { EditorSidebarComponent } from './editor-sidebar/editor-sidebar.component';
import { ContentCanvasComponent } from './content-canvas/content-canvas.component';
import { ContentSerializerService } from './content-serializer.service';

import { TreeInteractionService } from '../tree-interaction.service';
import { UndoRedoService } from '../undo-redo.service';
import { ControllerService } from '../shared/services/controller.service';

@Component({
  selector: 'app-content-editor',
  templateUrl: './content-editor.component.html',
  styleUrls: ['./content-editor.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CdkDropList,
    TreeComponent,
    BuildVersionsComponent,
    PropertiesPanelComponent,
    EditorToolbarComponent,
    EditorSidebarComponent,
    ContentCanvasComponent
  ]
})
export class ContentEditorComponent implements OnInit, OnDestroy {
  // Drop list ID for the sticky "add to end" zone in the target pane (dual pane).
  readonly appendDropZoneId = 'content-editor-append-dropzone';
  private readonly livePreviewStorageKey = 'hemisphere.livePreview.content';

  sidebarWidth = Math.min(520, Math.floor(window.innerWidth * 0.30));
  private isResizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  onResizeStart(event: MouseEvent) {
    this.isResizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.sidebarWidth;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onResizeMove(event: MouseEvent) {
    if (!this.isResizing) return;
    const delta = event.clientX - this.resizeStartX;
    const maxWidth = Math.floor(window.innerWidth * 0.30);
    this.sidebarWidth = Math.max(160, Math.min(maxWidth, this.resizeStartWidth + delta));
  }

  @HostListener('document:mouseup')
  onResizeEnd() {
    this.isResizing = false;
  }

  contentId: string;
  version: number;
  private _skipRouteReload = false;

  metadata: any = { name: 'New Content', type: 'training' };
  targetMetadata: any;
  originalMetadata: any;

  sourceHContent: HContent;

  sourceItems: TreeItem[] = [];
  targetItems: TreeItem[] = [];

  videos: Map<string, string>;

  isTemplateMode = false;
  contentSearchResults: any[] = [];
  isViewingDiff = false;

  searchTerm = '';
  private searchSubject = new Subject<string>();
  private contentSearchSubject = new Subject<string>();

  isDualPane = false;
  showNewTrainingPane = false;
  showResetConfirmation = false;
  showLoadLocationModal = false;
  showVersionsModal = false;
  showSaveOptionsModal = false;
  saveAsChild = false;
  saveAsTemplate = false;
  showImportSourceModal = false;
  importSourceText = '';
  importSourceError: string | null = null;

  mainSaveState: 'idle' | 'saving' | 'success' = 'idle';
  saveAsState: 'idle' | 'saving' | 'success' = 'idle';
  saveState: 'idle' | 'saving' | 'success' = 'idle';
  saveErrorMessage: string | null = null;
  copySourceJsonState: 'idle' | 'success' = 'idle';

  private loadLocationResolve: (value: 'source' | 'target' | 'cancel') => void;

  @ViewChild(PropertiesPanelComponent) propertiesPanel: PropertiesPanelComponent;

  selectedSourceRouteId: string | null = null;
  selectedTargetRouteId: string | null = null;
  selectedSourceComponentId: string | null = null;
  selectedTargetComponentId: string | null = null;

  newTrainingMetadata: any = {
    properties: { name: 'New Training', type: 'training', version: 0 }
  };

  // --- Computed state ---

  get currentMetadata(): any {
    return this.isDualPane ? (this.targetMetadata ?? this.metadata) : this.metadata;
  }

  get currentTitle(): string {
    return this.selectedSourceComponentDisplayTitle || this.selectedSourceRouteDisplayTitle
      || this.currentMetadata?.name || this.metadata?.name || 'Untitled content';
  }

  get currentTypeLabel(): string {
    return this.currentMetadata?.type || this.metadata?.type || 'content';
  }

  get sourceRoutes(): TreeItem[] {
    return this.sourceItems || [];
  }

  get targetRoutes(): TreeItem[] {
    return this.targetItems || [];
  }

  get selectedSourceRoute(): TreeItem | null {
    return this.findRouteById(this.sourceItems, this.selectedSourceRouteId);
  }

  get selectedTargetRoute(): TreeItem | null {
    return this.findRouteById(this.targetItems, this.selectedTargetRouteId);
  }

  get selectedSourceRouteDisplayTitle(): string {
    const route = this.selectedSourceRoute;
    return route ? this.getRouteDisplayTitle(route) : '';
  }

  get selectedSourceComponent(): TreeItem | null {
    return this.findComponentById(this.selectedSourceRoute, this.selectedSourceComponentId);
  }

  get selectedTargetComponent(): TreeItem | null {
    return this.findComponentById(this.selectedTargetRoute, this.selectedTargetComponentId);
  }

  get selectedSourceComponentDisplayTitle(): string {
    const component = this.selectedSourceComponent;
    return component ? this.getComponentDisplayTitle(component) : '';
  }

  get sourceEditorChildItems(): TreeItem[] {
    return this.selectedSourceComponent?.children || [];
  }

  get sourceEditorLevel(): number {
    return this.selectedSourceComponent ? this.selectedSourceComponent.level + 1 : 0;
  }

  // --- Selection helpers ---

  findRouteById(routes: TreeItem[], routeId: string | null): TreeItem | null {
    if (!routes?.length) return null;
    if (!routeId) return routes[0];
    return routes.find(route => route.id === routeId) ?? routes[0];
  }

  findComponentById(parent: TreeItem | null, componentId: string | null): TreeItem | null {
    if (!parent?.children?.length) return null;

    const componentItems = parent.children.filter(child =>
      child.level === TreeLevel.Component || child.level === TreeLevel.SubComponent
    );

    if (!componentItems.length) return null;
    if (!componentId) return componentItems[0];

    // Search the whole component/sub-component tree. Only fall back to the
    // first component when the id genuinely isn't found anywhere — the
    // fallback must NOT run inside the recursion, otherwise descending into an
    // earlier component returns that component's first sub-component as a false
    // positive and the search stops before reaching the real target.
    return this.searchComponentById(parent, componentId) ?? componentItems[0];
  }

  private searchComponentById(parent: TreeItem | null, componentId: string): TreeItem | null {
    if (!parent?.children?.length) return null;

    const componentItems = parent.children.filter(child =>
      child.level === TreeLevel.Component || child.level === TreeLevel.SubComponent
    );

    for (const item of componentItems) {
      if (item.id === componentId) return item;
      const nested = this.searchComponentById(item, componentId);
      if (nested) return nested;
    }

    return null;
  }

  ensureSelectedRoutes() {
    this.selectedSourceRouteId = this.findRouteById(this.sourceItems, this.selectedSourceRouteId)?.id ?? null;
    this.selectedTargetRouteId = this.findRouteById(
      this.targetItems,
      this.selectedTargetRouteId || this.selectedSourceRouteId
    )?.id ?? null;
    this.ensureSelectedComponents();
  }

  ensureSelectedComponents() {
    this.selectedSourceComponentId = this.findComponentById(
      this.selectedSourceRoute, this.selectedSourceComponentId
    )?.id ?? null;

    this.selectedTargetComponentId = this.findComponentById(
      this.selectedTargetRoute,
      this.selectedTargetComponentId || this.selectedSourceComponentId
    )?.id ?? null;

    const sourceRoute = this.selectedSourceRoute;
    if (sourceRoute && this.selectedSourceComponentId) {
      sourceRoute.expanded = true;
    }
  }

  selectSourceComponent(routeId: string, componentId: string) {
    this.selectedSourceRouteId = routeId;
    this.selectedSourceComponentId = componentId;

    if (this.isDualPane) {
      this.selectedTargetRouteId = this.findRouteById(this.targetItems, routeId)?.id ?? null;
      this.selectedTargetComponentId = this.findComponentById(this.selectedTargetRoute, componentId)?.id ?? null;
    }
  }

  getRouteDisplayTitle(route: TreeItem): string {
    const routePath = (route.item as any)?.route ?? route.tag ?? '';
    return this.formatLabel(routePath) || route.name || 'Untitled route';
  }

  getComponentDisplayTitle(component: TreeItem): string {
    const type = (component.item as any)?.type ?? component.tag ?? 'component';
    return this.formatLabel(type);
  }

  private formatLabel(value: string): string {
    if (!value) return '';
    return value
      .replace(/^\//, '')
      .replace(/\//g, ' / ')
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  // --- Sidebar events ---

  onSidebarItemClicked(item: TreeItem) {
    if (item.level === TreeLevel.Component || item.level === TreeLevel.SubComponent) {
      const route = this.findRouteForComponent(item.id);
      if (route) {
        route.expanded = true;
        this.selectSourceComponent(route.id, item.id);
      }
    }
  }

  findRouteForComponent(componentId: string): TreeItem | null {
    const contains = (children: TreeItem[]): boolean => {
      for (const child of children) {
        if (child.id === componentId) return true;
        if (child.children?.length && contains(child.children)) return true;
      }
      return false;
    };
    return this.sourceItems.find(route => contains(route.children || [])) ?? null;
  }

  // --- Dual pane ---

  toggleDualPane() {
    this.targetMetadata = this.targetMetadata ?? { ...this.metadata };
    this.isDualPane = !this.isDualPane;
    this.selectedSourceRouteId = null;
    this.selectedSourceComponentId = null;
    this.selectedTargetRouteId = null;
    this.selectedTargetComponentId = null;
  }

  copySourceToTarget() {
    this.targetMetadata = JSON.parse(JSON.stringify(this.metadata || this.newTrainingMetadata.properties || {}));
    this.targetItems = JSON.parse(JSON.stringify(this.sourceItems || []));
    this.isDualPane = true;
    this.selectedTargetRouteId = this.selectedSourceRouteId;
    this.selectedTargetComponentId = this.selectedSourceComponentId;
    this.ensureSelectedRoutes();
  }

  // --- Constructor & lifecycle ---

  constructor(
    private route: ActivatedRoute,
    private httpService: HttpService,
    private router: Router,
    private treeService: TreeDataService,
    public interactionService: TreeInteractionService,
    public undoRedoService: UndoRedoService,
    private controller: ControllerService,
    private serializer: ContentSerializerService,
    private dragDropService: DragDropService
  ) {
    this.undoRedoService.onBeforeMutation(() => this.captureSnapshot());
  }

  // Append a source route dropped onto the sticky end-of-list zone.
  onAppendRouteDrop(event: CdkDragDrop<TreeItem[]>) {
    const dragged = event.item.data as TreeItem;
    if (!dragged || dragged.level !== TreeLevel.Route) return;
    if (event.previousContainer === event.container) return;

    this.undoRedoService.notifyBeforeMutation();
    this.targetItems.push(this.treeService.cloneTemplate(dragged, TreeLevel.Route));
    this.onOrderChanged();
  }

  async ngOnInit() {
    // Make the sticky zone a valid drop target for source routes (level 0).
    this.dragDropService.register(this.appendDropZoneId, TreeLevel.Route, TreeType.Target);

    const videosResponse: any = await this.httpService.getAsync('hcontent/getVideos');
    if (videosResponse?.success) {
      this.videos = videosResponse.data.reduce((acc: Map<string, string>, video: any) => {
        acc.set(video.id, video.title);
        return acc;
      }, new Map<string, string>());
    }

    combineLatest([this.route.params, this.route.queryParams])
      .pipe(debounceTime(0))
      .subscribe(async ([params, queryParams]) => {
        this.contentId = params['id'];
        this.version = queryParams['version'] ? queryParams['version'] : undefined;

        if (this.contentId) {
          if (this._skipRouteReload) {
            this._skipRouteReload = false;
          } else {
            await this.fetchStructure();
          }
        }

        await this.fetchTemplates('');
      });

    this.searchSubject.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(term => this.performSearch(term));

    this.contentSearchSubject.pipe(debounceTime(300))
      .subscribe(term => this.fetchTemplates(term));
  }

  ngOnDestroy() {
    this.dragDropService.unregister(this.appendDropZoneId, TreeLevel.Route, TreeType.Target);
  }

  // --- Content search ---

  onContentSearchChanged(term: string) {
    this.contentSearchSubject.next(term);
  }

  toggleTemplateMode() {
    this.isTemplateMode = !this.isTemplateMode;
    this.contentSearchSubject.next('');
  }

  async fetchTemplates(term: string) {
    try {
      const res: any = await this.httpService.postAsync('builder/getTemplates', {
        search: term,
        template: this.isTemplateMode
      });
      if (res) {
        this.contentSearchResults = Array.isArray(res) ? res : (res.data || []);
      }
    } catch (e) {
      console.error('Error fetching templates', e);
      this.contentSearchResults = [];
    }
  }

  selectContent(item: any) {
    this.router.navigate(['/content-editor', item.id]);
  }

  // --- Data fetching ---

  async fetchStructure() {
    try {
      const response: any = await this.httpService.postAsync('builder/build', {
        hcontentId: this.contentId,
        version: this.version
      });

      const config = response.config;
      this.version = response.version;

      let target = 'source';
      if (this.isDualPane) {
        const choice = await this.askLoadLocation();
        if (choice === 'cancel') return;
        target = choice;
      }

      if (target === 'target') {
        this.targetMetadata = config.metadata?.properties ?? {};
        if (config?.sections) {
          this.targetItems = this.treeService.loadTraining(config, this.videos);
          this.ensureSelectedRoutes();
        }
      } else {
        this.metadata = config.metadata?.properties ?? {};
        this.originalMetadata = { ...this.metadata };
        if (config?.sections) {
          this.sourceItems = this.treeService.loadTraining(config, this.videos);
          this.ensureSelectedRoutes();
        }
      }

      this.undoRedoService.clear();
    } catch (e) {
      console.error('Error fetching content structure', e);
    }
  }

  async loadVersionWithDiffs(version: number) {
    const response: any = await this.httpService.postAsync('builder/getDiffs', {
      contentId: this.contentId,
      version
    });

    if (response.success) {
      this.isDualPane = false;
      this.isViewingDiff = true;
      this.sourceItems = this.treeService.loadTraining(response.data.config, this.videos);
      this.treeService.processOverrides(this.sourceItems, response.data.overrides);
    }
  }

  // --- Position tracking ---

  onOrderChanged() {
    this.updatePositionsRecursive(this.sourceItems);
    this.updatePositionsRecursive(this.targetItems);
  }

  private updatePositionsRecursive(items: TreeItem[]) {
    items.forEach((item, index) => {
      item.updatedPosition = index;
      item.isMoved = item.updatedPosition !== (item.position ?? -1);
      item.hardPosition = index;
    });
    items.forEach(item => {
      if (item.children?.length) this.updatePositionsRecursive(item.children);
    });
  }

  // --- Search ---

  onSearchChange(term: string) {
    this.searchTerm = term;
    this.searchSubject.next(term);
  }

  private performSearch(term: string) {
    if (!term) {
      this.collapseAll(this.sourceItems);
      this.collapseAll(this.targetItems);
      return;
    }
    const lowerTerm = term.toLowerCase();
    this.sourceItems.forEach(item => this.expandMatching(item, lowerTerm));
    this.targetItems.forEach(item => this.expandMatching(item, lowerTerm));
  }

  private collapseAll(items: TreeItem[]) {
    items.forEach(item => {
      item.expanded = false;
      if (item.children) this.collapseAll(item.children);
    });
  }

  private expandMatching(item: TreeItem, term: string): boolean {
    let anyChildMatched = false;
    if (item.children?.length) {
      item.children.forEach(child => {
        if (this.expandMatching(child, term)) anyChildMatched = true;
      });
    }
    const selfMatched = typeof item.name === 'string' && item.name.toLowerCase().includes(term);
    item.expanded = anyChildMatched;
    return selfMatched || anyChildMatched;
  }

  // --- Properties ---

  openMetadataProperties(event: MouseEvent) {
    this.propertiesPanel?.openMetadataProperties(event);
  }

  openSelectedComponentProperties(event: MouseEvent) {
    if (!this.selectedSourceComponent) return;
    event.stopPropagation();
    this.interactionService.openProperties(this.selectedSourceComponent, event);
  }

  openSelectedRouteProperties(event: MouseEvent) {
    if (!this.selectedSourceRoute) return;
    event.stopPropagation();
    this.interactionService.openProperties(this.selectedSourceRoute, event);
  }

  onMetadataPropertiesChange(metadata: any) {
    if (this.isDualPane) {
      this.targetMetadata = metadata;
    } else {
      this.metadata = metadata;
    }
  }

  // --- Versions ---

  toggleVersionsModal() {
    if (this.isViewingDiff) {
      this.isViewingDiff = false;
      this.fetchStructure();
    } else {
      this.showVersionsModal = !this.showVersionsModal;
    }
  }

  async onVersionSelected(version: any) {
    if (version?.id) {
      this.router.navigate(['/content-editor', version.hcontent_id], {
        queryParams: { version: version.version }
      });
    }
  }

  // --- Live preview ---

  livePreview() {
    const payload = this.serializer.buildPayload(
      this.isDualPane ? this.targetMetadata : this.metadata,
      this.isDualPane ? this.targetItems : this.sourceItems
    );
    localStorage.setItem(this.livePreviewStorageKey, JSON.stringify(payload));

    const selectedComponentId = this.isDualPane
      ? this.selectedTargetComponentId
      : this.selectedSourceComponentId;

    const queryParams: any = { livePreview: true };
    if (selectedComponentId) {
      const step = this.computeStepForComponent(payload, selectedComponentId);
      if (step != null) queryParams.step = step;
    }

    const previewUrl = this.router.serializeUrl(
      this.router.createUrlTree(['/start'], { queryParams })
    );
    window.open(previewUrl, '_blank', 'noopener');
  }

  async copySourceJson() {
    const payload = this.serializer.buildPayload(
      this.isDualPane ? this.targetMetadata : this.metadata,
      this.isDualPane ? this.targetItems : this.sourceItems
    );
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    this.copySourceJsonState = 'success';
    setTimeout(() => this.copySourceJsonState = 'idle', 1500);
  }

  openImportSourceModal() {
    this.importSourceText = '';
    this.importSourceError = null;
    this.showImportSourceModal = true;
  }

  closeImportSourceModal() {
    this.showImportSourceModal = false;
  }

  confirmImportSource() {
    let config: any;
    try {
      config = JSON.parse(this.importSourceText);
    } catch (e) {
      this.importSourceError = 'Invalid JSON. Please check the pasted content.';
      return;
    }

    if (!Array.isArray(config?.sections)) {
      this.importSourceError = 'JSON is missing a "sections" array.';
      return;
    }

    this.undoRedoService.notifyBeforeMutation();

    // Existing routes aren't just discarded: previously-saved ones are soft-deleted
    // (kept, flagged) so a removal override is generated; unsaved new ones are dropped
    // outright, mirroring TreeComponent.remove(). The imported routes are cloned through
    // cloneTemplate to get fresh ids and isNew flags, since generateOverrides only picks
    // up items marked new/deleted/moved/updated - plain replacement would look unchanged.
    const removedRoutes = (this.sourceItems || [])
      .filter(route => !route.isNew)
      .map(route => { route.isDeleted = true; return route; });

    const importedRoutes = this.treeService.loadTraining(config, this.videos)
      .map(route => this.treeService.cloneTemplate(route, TreeLevel.Route));

    this.sourceItems = [...removedRoutes, ...importedRoutes];
    this.ensureSelectedRoutes();
    this.onOrderChanged();
    this.showImportSourceModal = false;
  }

  private computeStepForComponent(payload: any, targetComponentId: string): number | null {
    let stepIndex = 0;
    for (const section of (payload.sections ?? [])) {
      const components: any[] = section.components ?? [];
      if (components.length === 0) {
        stepIndex++;
        continue;
      }
      for (const component of components) {
        if (component.componentId === targetComponentId) {
          return stepIndex;
        }
        stepIndex += component.subComponents?.length > 0 ? component.subComponents.length : 1;
      }
    }
    return null;
  }

  // --- Save ---

  async openSaveAsModal() {
    this.saveAsChild = false;
    this.saveAsTemplate = false;
    this.showSaveOptionsModal = true;
  }

  closeSaveModal() {
    this.showSaveOptionsModal = false;
  }

  toggleSaveOption(option: 'saveAsChild' | 'saveAsTemplate') {
    if (option === 'saveAsChild') {
      this.saveAsChild = !this.saveAsChild;
      if (this.saveAsChild) this.saveAsTemplate = false;
    } else {
      this.saveAsTemplate = !this.saveAsTemplate;
      if (this.saveAsTemplate) this.saveAsChild = false;
    }
  }

  isSaveModalDisabled(): boolean {
    return !this.currentMetadata?.name?.trim();
  }

  async confirmSave(source: 'main' | 'saveAs' = 'main') {
    this.closeSaveModal();
    this.saveErrorMessage = null;

    if (source === 'main') {
      this.mainSaveState = 'saving';
      this.saveAsChild = false;
      this.saveAsTemplate = false;
    } else {
      this.saveAsState = 'saving';
    }

    const items = this.isDualPane ? this.targetItems : this.sourceItems;
    const overrides = this.treeService.generateOverrides(items);
    let id: string | null = this.contentId;
    let metadata = this.isDualPane ? this.targetMetadata : this.metadata;

    if (this.isDualPane) {
      id = null;
      metadata.builtFromId = metadata.builtFromId ?? this.contentId;
      metadata.builtFromVersion = this.version;
    } else if (source === 'saveAs' && !this.saveAsChild && !this.saveAsTemplate) {
      // Plain "Save As" with no relation chosen: save under the new name as an
      // independent piece of content rather than overwriting the original.
      id = null;
    }
    if (this.saveAsTemplate) {
      metadata.template = true;
    }

    const payload = this.serializer.buildPayload(
      this.isDualPane ? this.targetMetadata : this.metadata,
      this.isDualPane ? this.targetItems : this.sourceItems
    );

    try {
      const resp: any = await this.httpService.postAsync('builder/saveContent', {
        id,
        overrides,
        content: payload,
        newBuild: this.isDualPane,
        saveAsTemplate: this.saveAsTemplate,
        saveAsChild: this.saveAsChild,
        properties: metadata
      });

      if (resp.success) {
        if (source === 'main') {
          this.mainSaveState = 'success';
          setTimeout(() => this.mainSaveState = 'idle', 2000);
        } else {
          this.saveAsState = 'success';
          setTimeout(() => this.saveAsState = 'idle', 2000);
        }

        this.contentId = resp.id;
        this.version = resp.version;
        this.isDualPane = false;
        this._skipRouteReload = true;

        this.router.navigate(['/content-editor', resp.id], {
          queryParams: { version: resp.version },
          replaceUrl: true
        });

        await this.fetchStructure();
      } else {
        throw new Error(resp.message || 'Failed to save content.');
      }
    } catch (e) {
      console.error('Save failed', e);
      this.saveErrorMessage = e?.message || 'Failed to save content.';
      if (source === 'main') this.mainSaveState = 'idle';
      else this.saveAsState = 'idle';
    }
  }

  dismissSaveError() {
    this.saveErrorMessage = null;
  }

  // --- New training ---

  toggleNewTrainingPane() {
    if (!this.showNewTrainingPane) {
      this.showNewTrainingPane = true;
      this.isDualPane = true;
      if (!this.targetMetadata) {
        this.targetMetadata = JSON.parse(JSON.stringify(this.newTrainingMetadata.properties || {}));
      }
    } else {
      this.showResetConfirmation = true;
    }
  }

  closeNewTrainingPane() {
    this.showNewTrainingPane = false;
  }

  closeResetModal() {
    this.showResetConfirmation = false;
  }

  confirmReset() {
    this.showResetConfirmation = false;
    this.showNewTrainingPane = true;
    this.isDualPane = true;
    this.targetMetadata = JSON.parse(JSON.stringify(this.newTrainingMetadata.properties || {}));
    this.targetItems = [];
    this.selectedTargetRouteId = null;
    this.selectedTargetComponentId = null;
  }

  async saveNewTraining() {
    if (!this.isDualPane) {
      this.copySourceToTarget();
    }
    await this.confirmSave('main');
  }

  // --- Load location modal ---

  askLoadLocation(): Promise<'source' | 'target' | 'cancel'> {
    this.showLoadLocationModal = true;
    return new Promise(resolve => {
      this.loadLocationResolve = resolve;
    });
  }

  chooseLoadLocation(location: 'source' | 'target') {
    this.showLoadLocationModal = false;
    if (this.loadLocationResolve) {
      this.loadLocationResolve(location);
      this.loadLocationResolve = null as any;
    }
  }

  cancelLoadLocation() {
    this.showLoadLocationModal = false;
    if (this.loadLocationResolve) {
      this.loadLocationResolve('cancel');
      this.loadLocationResolve = null as any;
    }
  }

  // --- Undo / Redo ---

  private captureSnapshot() {
    const items = this.isDualPane ? this.targetItems : this.sourceItems;
    this.undoRedoService.pushState(items);
  }

  undo() {
    const items = this.isDualPane ? this.targetItems : this.sourceItems;
    const restored = this.undoRedoService.undo(items);
    if (restored) {
      if (this.isDualPane) this.targetItems = restored;
      else this.sourceItems = restored;
      this.interactionService.editingItem.set(null);
    }
  }

  redo() {
    const items = this.isDualPane ? this.targetItems : this.sourceItems;
    const restored = this.undoRedoService.redo(items);
    if (restored) {
      if (this.isDualPane) this.targetItems = restored;
      else this.sourceItems = restored;
      this.interactionService.editingItem.set(null);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    if (isCtrlOrCmd && event.key === 'z' && !event.shiftKey) {
      event.preventDefault();
      this.undo();
    } else if (isCtrlOrCmd && event.key === 'z' && event.shiftKey) {
      event.preventDefault();
      this.redo();
    }
  }
}
